import React from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../../hooks/useScrollEdgeFade';
import {
  getListsSectionRootSx,
  listsSectionHeaderSx,
  listsSectionTitleSx,
  listsSectionChevronSx,
  listsSectionBodySx,
  listsSectionBodyFillSx,
  listsSectionScrollContentSx,
  listsSectionToolbarWrapSx,
  listsSectionEmptyWrapSx,
} from './UserLotListsPanel.styles';

const getCollapseFillSx = (shouldFill) => (
  shouldFill
    ? {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      '& > .MuiCollapse-wrapper': {
        flex: 1,
        minHeight: 0,
      },
      '& > .MuiCollapse-wrapper > .MuiCollapse-wrapperInner': {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      },
    }
    : undefined
);

/**
 * Shell colapsable para bloques de listas (Mis listas / Compartidas).
 * Presentacional: el contenido (cards) lo inyecta el padre vía children.
 * `toolbar` se renderiza dentro del colapso (p. ej. chips de filtro de Mis listas).
 * `count` muestra un badge numérico junto al título (opcional).
 * Con `fillHeight`, la sección expandida ocupa el alto disponible del panel.
 * La zona que scrollea lleva el fade de bordes (mismo uso que Historial); el
 * hook la observa, así que expandir / colapsar o cargar datos lo recalcula.
 */
export default function ListsCollapsibleSection({
  title,
  count = null,
  expanded = true,
  onToggle,
  children,
  emptyContent = null,
  hasItems = true,
  toolbar = null,
  bodySx = null,
  fillHeight = false,
}) {
  const shouldFill = fillHeight && expanded;
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();

  return (
    <Box sx={getListsSectionRootSx(expanded, { fillHeight })}>
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        sx={listsSectionHeaderSx(expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography component="span" sx={listsSectionTitleSx}>
            {title}
          </Typography>
          {count != null && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 22,
                height: 22,
                px: '6px',
                borderRadius: '4px',
                bgcolor: '#141414',
                border: '1px solid #383838',
                color: '#D4D4D4',
                fontSize: '12px',
                fontWeight: 600,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              {count}
            </Box>
          )}
        </Box>
        <ExpandMoreIcon fontSize="small" sx={listsSectionChevronSx(expanded)} />
      </Box>

      <Collapse
        in={expanded}
        timeout="auto"
        unmountOnExit={false}
        sx={getCollapseFillSx(shouldFill)}
      >
        <Box
          sx={{
            ...listsSectionBodySx,
            ...(shouldFill ? listsSectionBodyFillSx : {}),
            ...bodySx,
          }}
        >
          {toolbar ? (
            <Box sx={listsSectionToolbarWrapSx}>{toolbar}</Box>
          ) : null}
          <Box
            ref={scrollRef}
            data-testid="lists-section-scroll"
            sx={{
              ...listsSectionScrollContentSx,
              ...(toolbar ? { mt: '8px' } : {}),
              ...(shouldFill ? {} : { flex: '0 1 auto', minHeight: 'auto' }),
              ...(!hasItems && shouldFill ? {
                display: 'flex',
                flexDirection: 'column',
              } : {}),
              ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
            }}
          >
            {hasItems ? children : (
              <Box sx={listsSectionEmptyWrapSx}>
                {emptyContent}
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
